import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  EmptyState,
  InlineStack,
  Select,
  Spinner,
  Text
} from '@shopify/polaris';
import { adminApi } from '../../services/api/adminApi';

const formatProvider = (provider) => {
  const map = {
    adobe: 'Adobe Commerce',
    shopify: 'Shopify',
    wayfair: 'Wayfair',
    wayfairagent: 'Wayfair Agent',
    netsuite: 'NetSuite',
    sage: 'SAGE 300',
    quickbooks: 'QuickBooks Online',
    bc: 'Business Central',
    kds: 'KDS'
  };
  return map[provider] || provider || '-';
};

export const ProvisioningWizard = () => {
  const [step, setStep] = useState(1);
  const [tenants, setTenants] = useState([]);
  const [sourceServices, setSourceServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [enableCopiedServices, setEnableCopiedServices] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [provisionResult, setProvisionResult] = useState(null);
  const [wizardData, setWizardData] = useState({
    targetTenantId: '',
    sourceTenantId: ''
  });

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getTenants({ status: 'active', limit: 1000 });
      if (response.success) {
        setTenants(response.tenants || []);
      } else {
        setError(response.error || 'Failed to fetch tenants');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCloneableServices = useCallback(async (sourceTenantId) => {
    if (!sourceTenantId) {
      setSourceServices([]);
      return;
    }

    try {
      setLoadingServices(true);
      setError(null);
      const response = await adminApi.getCloneableServices(sourceTenantId);
      if (response.success) {
        setSourceServices(response.services || []);
        setSelectedServices([]);
      } else {
        setError(response.error || 'Failed to fetch source services');
      }
    } catch (err) {
      setSourceServices([]);
      setSelectedServices([]);
      setError(err.message || 'Failed to fetch source services');
    } finally {
      setLoadingServices(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    if (step === 3 && wizardData.sourceTenantId) {
      fetchCloneableServices(wizardData.sourceTenantId);
    }
  }, [step, wizardData.sourceTenantId, fetchCloneableServices]);

  const tenantOptions = useMemo(() => ([
    { label: 'Select tenant...', value: '' },
    ...tenants.map(tenant => ({
      label: `${tenant.name} (${tenant.environment})`,
      value: tenant.id
    }))
  ]), [tenants]);

  const targetTenant = tenants.find(tenant => tenant.id === wizardData.targetTenantId);
  const sourceTenant = tenants.find(tenant => tenant.id === wizardData.sourceTenantId);
  const copyableServices = sourceServices.filter(service => service.hasTenantConfig && service.hasFlowConfig);
  const selectedServiceDetails = sourceServices.filter(service => selectedServices.includes(service.key));

  const handleNext = () => {
    if (step === 1 && !wizardData.targetTenantId) {
      setError('Please select a target tenant');
      return;
    }
    if (step === 2 && !wizardData.sourceTenantId) {
      setError('Please select a source tenant');
      return;
    }
    if (step === 3 && selectedServices.length === 0) {
      setError('Please select at least one service');
      return;
    }
    setError(null);
    setStep(step + 1);
  };

  const handleBack = () => {
    setError(null);
    setStep(step - 1);
  };

  const handleReset = () => {
    setStep(1);
    setWizardData({ targetTenantId: '', sourceTenantId: '' });
    setSourceServices([]);
    setSelectedServices([]);
    setEnableCopiedServices(true);
    setProvisionResult(null);
    setSuccess(null);
    setError(null);
  };

  const handleServiceToggle = (serviceKey) => {
    setSelectedServices(prev => (
      prev.includes(serviceKey)
        ? prev.filter(key => key !== serviceKey)
        : [...prev, serviceKey]
    ));
  };

  const handleSelectAll = () => {
    if (selectedServices.length === copyableServices.length) {
      setSelectedServices([]);
    } else {
      setSelectedServices(copyableServices.map(service => service.key));
    }
  };

  const handleProvision = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setProvisionResult(null);

      const response = await adminApi.copyServicesFromTenant(wizardData.targetTenantId, {
        sourceTenantId: wizardData.sourceTenantId,
        services: selectedServices,
        enableCopiedServices
      });

      if (response.success) {
        setProvisionResult(response);
        setSuccess(response.message || `Copied ${response.copied?.length || 0} service(s)`);
        setStep(5);
      } else {
        setProvisionResult(response);
        setError(response.error || response.message || 'Provisioning completed with errors');
      }
    } catch (err) {
      setError(err.message || 'Failed to provision services');
    } finally {
      setLoading(false);
    }
  };

  const renderServiceRow = (service, index) => {
    const disabled = !service.hasTenantConfig || !service.hasFlowConfig;
    return (
      <div
        key={service.key}
        style={{
          display: 'grid',
          gridTemplateColumns: '32px minmax(140px, 1.1fr) minmax(120px, 0.8fr) minmax(120px, 0.8fr) minmax(90px, 0.6fr) minmax(130px, 1fr)',
          gap: '12px',
          alignItems: 'center',
          padding: '10px 16px',
          borderBottom: index < sourceServices.length - 1 ? '1px solid #e1e3e5' : 'none',
          opacity: disabled ? 0.55 : 1
        }}
      >
        <Checkbox
          label=""
          checked={selectedServices.includes(service.key)}
          onChange={() => handleServiceToggle(service.key)}
          disabled={disabled}
        />
        <Text fontWeight="semibold">{service.label || service.key}</Text>
        <Text tone="subdued">{formatProvider(service.sourceProvider)}</Text>
        <Text tone="subdued">{formatProvider(service.destination)}</Text>
        <Badge tone={service.enabled ? 'success' : 'subdued'}>
          {service.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
        <InlineStack gap="tight">
          <Badge tone={service.hasTenantConfig ? 'success' : 'critical'}>DTO</Badge>
          <Badge tone={service.hasFlowConfig ? 'success' : 'critical'}>Flow</Badge>
        </InlineStack>
      </div>
    );
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <BlockStack gap="base">
            <BlockStack gap="tight">
              <Text variant="headingMd">Target Tenant</Text>
              <Text tone="subdued">Choose the tenant that should receive copied service configs.</Text>
            </BlockStack>
            <Select
              label="Target tenant"
              options={tenantOptions}
              value={wizardData.targetTenantId}
              onChange={(value) => setWizardData(prev => ({ ...prev, targetTenantId: value }))}
            />
            {targetTenant && (
              <Card>
                <BlockStack gap="tight">
                  <Text variant="headingSm">{targetTenant.name}</Text>
                  <Text tone="subdued">Environment: {targetTenant.environment}</Text>
                  <Text tone="subdued">Owner: {targetTenant.owner?.name || '-'}</Text>
                </BlockStack>
              </Card>
            )}
          </BlockStack>
        );

      case 2:
        return (
          <BlockStack gap="base">
            <BlockStack gap="tight">
              <Text variant="headingMd">Copy From Tenant</Text>
              <Text tone="subdued">Choose an existing tenant with working DTOs and FlowConfigs.</Text>
            </BlockStack>
            <Select
              label="Source tenant"
              options={tenantOptions.filter(option => option.value !== wizardData.targetTenantId)}
              value={wizardData.sourceTenantId}
              onChange={(value) => setWizardData(prev => ({ ...prev, sourceTenantId: value }))}
            />
            {sourceTenant && (
              <Card>
                <BlockStack gap="tight">
                  <Text variant="headingSm">{sourceTenant.name}</Text>
                  <Text tone="subdued">Environment: {sourceTenant.environment}</Text>
                  <Text tone="subdued">
                    Provider pair: {formatProvider(sourceTenant.integrations?.frontend?.provider)} to {formatProvider(sourceTenant.integrations?.backend?.provider)}
                  </Text>
                </BlockStack>
              </Card>
            )}
          </BlockStack>
        );

      case 3:
        return (
          <BlockStack gap="base">
            <BlockStack gap="tight">
              <Text variant="headingMd">Select Services</Text>
              <Text tone="subdued">These are real services from the source tenant. Credentials and schedules are not copied.</Text>
            </BlockStack>
            <InlineStack align="space-between" blockAlign="center">
              <Button onClick={handleSelectAll} disabled={copyableServices.length === 0}>
                {selectedServices.length === copyableServices.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Text>{selectedServices.length} of {copyableServices.length} selected</Text>
            </InlineStack>
            {loadingServices ? (
              <BlockStack align="center">
                <Spinner size="large" />
              </BlockStack>
            ) : sourceServices.length === 0 ? (
              <EmptyState
                heading="No source services found"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>The selected source tenant has no FlowConfig-backed services to copy.</p>
              </EmptyState>
            ) : (
              <Card>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '32px minmax(140px, 1.1fr) minmax(120px, 0.8fr) minmax(120px, 0.8fr) minmax(90px, 0.6fr) minmax(130px, 1fr)',
                    gap: '12px',
                    padding: '10px 16px',
                    borderBottom: '1px solid #e1e3e5',
                    fontWeight: 600,
                    fontSize: '13px'
                  }}
                >
                  <span />
                  <span>Service</span>
                  <span>Source</span>
                  <span>Destination</span>
                  <span>Status</span>
                  <span>Config</span>
                </div>
                {sourceServices.map(renderServiceRow)}
              </Card>
            )}
            <Checkbox
              label="Enable copied services"
              helpText="Schedules are not copied or created. This only controls service availability for manual and webhook runs."
              checked={enableCopiedServices}
              onChange={setEnableCopiedServices}
            />
          </BlockStack>
        );

      case 4:
        return (
          <BlockStack gap="base">
            <BlockStack gap="tight">
              <Text variant="headingMd">Confirm Provisioning</Text>
              <Text tone="subdued">Review the service copy before applying it to the target tenant.</Text>
            </BlockStack>
            <Card>
              <BlockStack gap="base">
                <Text><strong>Target:</strong> {targetTenant?.name}</Text>
                <Text><strong>Source:</strong> {sourceTenant?.name}</Text>
                <Text><strong>Copied services enabled:</strong> {enableCopiedServices ? 'Yes' : 'No'}</Text>
                <Text><strong>Credentials:</strong> Not copied</Text>
                <Text><strong>Schedules:</strong> Not copied</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="tight">
                <Text variant="headingSm">Services ({selectedServiceDetails.length})</Text>
                {selectedServiceDetails.map(service => (
                  <InlineStack key={service.key} gap="tight" blockAlign="center">
                    <Badge>{service.label || service.key}</Badge>
                    <Text tone="subdued">{formatProvider(service.sourceProvider)} to {formatProvider(service.destination)}</Text>
                  </InlineStack>
                ))}
              </BlockStack>
            </Card>
          </BlockStack>
        );

      case 5:
        return (
          <BlockStack gap="base">
            <BlockStack gap="tight">
              <Text variant="headingMd">Provisioning Complete</Text>
              <Text tone="subdued">Service DTOs and FlowConfigs were copied. Configure credentials before running flows.</Text>
            </BlockStack>
            <Card>
              <BlockStack gap="base">
                <Text><strong>Copied:</strong> {(provisionResult?.copied || []).join(', ') || '-'}</Text>
                <Text><strong>Credential dialogs needed:</strong> {(provisionResult?.requiredProviders || []).map(formatProvider).join(', ') || '-'}</Text>
                {provisionResult?.skipped?.length > 0 && (
                  <Text tone="critical">
                    Skipped: {provisionResult.skipped.map(item => `${item.key} (${item.reason})`).join(', ')}
                  </Text>
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        );

      default:
        return null;
    }
  };

  return (
    <BlockStack gap="loose">
      {error && (
        <Banner status="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}
      {success && (
        <Banner status="success" onDismiss={() => setSuccess(null)}>
          {success}
        </Banner>
      )}

      <Card>
        <BlockStack gap="loose">
          <BlockStack gap="tight">
            <Text variant="headingMd">Provisioning Wizard</Text>
            {step < 5 && <Text tone="subdued">Step {step} of 4</Text>}
          </BlockStack>

          {loading && step === 1 ? (
            <BlockStack align="center">
              <Spinner size="large" />
            </BlockStack>
          ) : (
            renderStep()
          )}

          <InlineStack align="end" gap="tight">
            {step > 1 && step < 5 && <Button onClick={handleBack}>Back</Button>}
            {step < 4 && (
              <Button primary onClick={handleNext}>
                Next
              </Button>
            )}
            {step === 4 && (
              <Button primary onClick={handleProvision} loading={loading}>
                Provision Services
              </Button>
            )}
            {step === 5 && (
              <Button primary onClick={handleReset}>
                Start New Provisioning
              </Button>
            )}
          </InlineStack>
        </BlockStack>
      </Card>
    </BlockStack>
  );
};
